<template>
  <div class="pb-6 sm:pb-18">
    <div
      id="overview"
      :ref="(el) => {
        steps[0] = el as HTMLElement
      }"
    >
      <HeroProduct :data="product" />
    </div>

    <Container
      tag="div"
      class="
        sticky top-navbar-height z-10 flex flex-wrap gap-x-12 border-b border-grey-100 bg-white font-semibold
        typo-subtitle
        max-md:hidden
      "
    >
      <template
        v-for="(step, index) in sections"
        :key="step.id"
      >
        <NuxtLink
          v-show="step.shouldShow"
          :to="`#${step.id}`"
          :class="{ 'text-primary-900': activeStep === index }"
          class="py-3 md:py-6"
        >
          {{ $t(step.title) }}
        </NuxtLink>
      </template>
    </Container>

    <!-- HIGHLIGHTS -->
    <div
      v-show="factsData.facts.length"
      id="highlights"
      :ref="(el) => {
        steps[1] = el as HTMLElement
      }"
    >
      <Facts :data="factsData" />
    </div>

    <!-- APPLICATIONS AND METHODS -->
    <div
      v-show="listingData.list_items.length"
      id="applications_and_methods"
      :ref="(el) => {
        steps[2] = el as HTMLElement
      }"
    >
      <Listing :data="listingData" />
    </div>

    <div

      id="technical-data"
      :ref="(el) => {
        steps[3] = el as HTMLElement
      }"
    >
      <ContentSection v-show="tableData.table_data.length || collapseData.length">
        <!-- TECHNICAL DATA -->
        <Container v-show="tableData.table_data.length">
          <DetailsSummary :duration-open="500">
            <template #summary>
              <div>
                <h2 v-html="tableData.heading" />
              </div>
            </template>

            <!-- Content -->
            <template #content>
              <div class="grid gap-x-14" role="table" :class="{ 'xl:grid-cols-2': tableData.table_data.length > 5 }">
                <div
                  v-for="definition in tableData.table_data"
                  :key="definition.id"
                  class="
                    grid grid-cols-12 gap-x-5 border-b border-grey-200 px-5
                    last-of-type:border-none
                    max-sm:flex max-sm:flex-col
                  "
                  role="row"
                >
                  <h3 class="col-span-5 pt-2 align-top max-sm:mb-1 max-xl:hyphens-auto sm:py-2" role="cell">
                    {{ definition.term }}
                  </h3>
                  <div class="col-span-7 w-full pb-2 align-top sm:py-2" role="cell">
                    <ul class="table-lists">
                      <li
                        v-for="description, index in definition.descriptions"
                        :key="definition.id + '-' + index"
                        class="font-semibold"
                      >
                        <div v-html="description ? description.toString() : ''" />
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </template>
          </DetailsSummary>
        </Container>

        <!-- COLLAPSE -->
        <Container v-show="collapseData.length" class="space-y-5 pt-5">
          <DetailsSummary v-for="item in collapseData" :key="item.id">
            <template #summary>
              <div>
                <h3>
                  {{ item.term }}
                </h3>
              </div>
            </template>

            <!-- Content -->
            <template #content>
              <div class="px-4 py-3 wysiwyg" v-html="item.text" />
            </template>
          </DetailsSummary>
        </Container>
      </ContentSection>
    </div>

    <!-- DOWNLOADS -->
    <div
      v-show="downloadsData.length"
      id="downloads"
      :ref="(el) => {
        steps[4] = el as HTMLElement
      }"
    >
      <ContentSection>
        <Container>
          <h2 class="mb-9 font-semibold typo-h3">
            {{ $t('product.downloads') }}
          </h2>

          <ul class="grid grid-cols-1 gap-x-7 gap-y-6 sm:grid-cols-[repeat(auto-fill,_minmax(21.5rem,_1fr))]">
            <li
              v-for="download in downloadsData"
              :key="download.id"
            >
              <Download class="h-full" :download="download" />
            </li>
          </ul>
        </Container>
      </ContentSection>
    </div>

    <!-- ACCESSORIES -->
    <div
      v-show="hasAccesories"
      id="accesories"
      :ref="(el) => {
        steps[5] = el as HTMLElement
      }"
    >
      <div>
        <TeaserProducts
          v-for="accessory in accessoriesData"
          :key="accessory.id"
          :data="accessory"
          slim
        />
      </div>
    </div>
    
  </div>
</template>

<script lang="ts" setup>
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

interface File {
  id: string
  url: string
  media_name: string
  languages: string[]
}

interface Accessory {
  id: string
  heading: string
  title: string
  products: Array<{
    id: string
    name: string
  }>
}

type Files = File[]

gsap.registerPlugin(ScrollTrigger)

const props = defineProps({
  product: {
    type: Object,
    default: () => ({}),
  },
})
const showCustomFields = ref(false)
const { t } = useI18n()

const sections = computed(() => {
  return [
    {
      id: 'overview',
      title: 'product.overview',
      shouldShow: true,
    },
    {
      id: 'highlights',
      title: 'product.facts.heading',
      shouldShow: factsData.value.facts.length,
    },
    {
      id: 'applications_and_methods',
      title: 'product.applications_and_methods',
      shouldShow: !!listingData.value.list_items.length,
    },
    {
      id: 'technical-data',
      title: 'product.table.heading',
      shouldShow: !!tableData.value.table_data.length,
    },
    {
      id: 'downloads',
      title: 'product.downloads',
      shouldShow: !!downloadsData.value.length,
    },
    {
      id: 'accesories',
      title: 'product.accesories',
      shouldShow: !!accessoriesData.value.length,
    },
  ]
})

const productTranslatedCustomFields = computed(() => {
  return props.product.translated?.customFields || {}
})

// HIGHLIGHTS
const factsData = computed(() => {
  // Convert PIM fields to an array of facts
  const facts = Object.keys(productTranslatedCustomFields.value)
    .filter(key => key.startsWith('PIM_PRODUCT_CUSTOM_FIELD_usp'))
    .map((key) => {
      const uspIndex = key.match(/PIM_PRODUCT_CUSTOM_FIELD_usp(\d+)/)[1]

      const heading = productTranslatedCustomFields.value[key].match(/<strong>(.*?)<\/strong>/)?.[1] // Extract heading
      const text = productTranslatedCustomFields.value[key]
        .replace(/<strong>.*?<\/strong>/, '') // Remove the <strong> tags
        .replace(/<p>\s*<\/p>/g, '') // Remove empty <p> tags

      const imageKey = `PIM_PRODUCT_ATTACHMENT_CUSTOM_FIELD_usp${uspIndex}bild_0_URL`
      const image = productTranslatedCustomFields.value[imageKey]

      return {
        id: uspIndex,
        heading: heading,
        text: text,
        image: image,
      }
    })

  return {
    type: 'facts',
    heading: t('product.facts.heading'),
    facts: facts,
  }
})

// APPLICATIONS AND METHODS
const listingData = computed(() => {
  const items = Object.keys(productTranslatedCustomFields.value)
    .filter(key =>
      (key.startsWith('PIM_PRODUCT_ATTACHMENT_CUSTOM_FIELD_piktogrammeanwendung_') || key.startsWith('PIM_PRODUCT_ATTACHMENT_CUSTOM_FIELD_piktogrammemethoden_'))
      && !key.includes('_URL')
      && !key.includes('_FILENAME'))
    .map((key) => {
    // Extract the index from the base key (e.g., '0' from 'piktogrammeanwendung_0')
      const indexMatch = key.match(/PIM_PRODUCT_ATTACHMENT_CUSTOM_FIELD_(piktogrammeanwendung|piktogrammemethoden)_(\d+)/)

      // If no match is found, skip this key
      if (!indexMatch) return null

      const index = indexMatch[2] // Extract the numeric index part
      const prefix = indexMatch[1] // Capture the prefix (e.g., 'piktogrammeanwendung' or 'piktogrammemethoden')

      // Construct the corresponding URL key based on the prefix
      const imageKey = `PIM_PRODUCT_ATTACHMENT_CUSTOM_FIELD_${prefix}_${index}_URL`

      // Get the image URL from the corresponding URL key
      const image = productTranslatedCustomFields.value[imageKey] || null

      // Get the filename and resolve the corresponding title and text
      const filename = productTranslatedCustomFields.value[`PIM_PRODUCT_ATTACHMENT_CUSTOM_FIELD_${prefix}_${index}_FILENAME`] || ''
      const cleanFilename = filename.replace(/\.[^.]*$/, '')
      const title = t(`product.applications_and_methods_texts.${cleanFilename}.title`)
      const text = t(`product.applications_and_methods_texts.${cleanFilename}.text`)

      return {
        id: index,
        group_image: {
          image: image,
        },
        group_text: {
          title: title,
          text: text,
        },
      }
    })

  return {
    heading: t('product.listing.heading'),
    list_items: items,
  }
})

// TECHNICAL DATA
const tableData = computed(() => {
  const filteredFields = Object.entries(productTranslatedCustomFields.value)
    .filter(([key]) =>
      !key.includes('PIM_PRODUCT_ATTACHMENT')
      && !key.includes('PerfionSystemId')
      && !key.includes('usp')
      && !key.includes('sku1')
      && !key.includes('methodenDBHaftungsausschluss')
      && !key.includes('aufmachertxt')
      && !key.includes('einsatztxt')
      && !key.includes('gutzuwissentxt')
      && !key.includes('Grundausstattung')
      && !key.includes('beachtentxt'))

    .map(([key, value]) => {
      const term = props.product.customFieldLabels[key]

      // Ensure value is a string before applying .replace()
      const cleanValue = typeof value === 'string'
        ? value.replace(/ style="[^"]*"/gi, '') // Remove inline styles
        : value

      // Try to parse JSON values, fallback to the original value if parsing fails
      let parsedValue
      try {
        parsedValue = JSON.parse(cleanValue)
      } catch {
        parsedValue = cleanValue
      }

      return {
        id: key,
        term: term || key,
        descriptions: Array.isArray(parsedValue) ? parsedValue : [parsedValue],
      }
    })

  // Define the prioritized IDs
  const prioritizedIds = [
    'PIM_PRODUCT_CUSTOM_FIELD_holzbeläge',
    'PIM_PRODUCT_CUSTOM_FIELD_kunstnaturstein',
    'PIM_PRODUCT_CUSTOM_FIELD_kunststoffbeläge',
  ]

  // Sort the filteredFields based on the prioritized IDs
  filteredFields.sort((a, b) => {
    const aIndex = prioritizedIds.indexOf(a.id)
    const bIndex = prioritizedIds.indexOf(b.id)

    if (aIndex !== -1 && bIndex !== -1) {
      // Both are in the prioritized list, sort by their order in the list
      return aIndex - bIndex
    } else if (aIndex !== -1) {
      // a is in the prioritized list, b is not
      return -1
    } else if (bIndex !== -1) {
      // b is in the prioritized list, a is not
      return 1
    } else {
      // Neither is in the prioritized list, maintain original order
      return 0
    }
  })

  return {
    heading: t('product.table.heading'),
    table_data: filteredFields,
  }
})

// COLLAPSE
const collapseData = computed(() => {
  const items = Object.entries(productTranslatedCustomFields.value)
    .filter(([key]) =>
      [
        'PIM_PRODUCT_CUSTOM_FIELD_beachtentxt',
        'PIM_PRODUCT_CUSTOM_FIELD_einsatztxt',
        'PIM_PRODUCT_CUSTOM_FIELD_gutzuwissentxt',
        'PIM_PRODUCT_CUSTOM_FIELD_methodenDBHaftungsausschluss',
        'PIM_PRODUCT_CUSTOM_FIELD_Grundausstattung',
      ].includes(key))
    .map(([key, value]) => {
      const term = props.product.customFieldLabels[key]
      const text = value ? value.replace(/ style="[^"]*"/gi, '') : '' // Remove inline styles

      // Try to parse JSON values, fallback to the original value if parsing fails
      let parsedText
      try {
        parsedText = JSON.parse(text)
      } catch {
        parsedText = text
      }

      return {
        id: key,
        term: term || key,
        text: parsedText,
      }
    })

  return items
})

// DOWNLOADS
const downloadsData = computed(() => {
  // Currently we assume that all fields that start with PIM_PRODUCT_ATTACHMENT_CUSTOM_FIELD_sdb are downloads and belongs to the same download group. As soon as we have more than one group, we need to extend this logic to distinguish between the groups.

  const relevantItems = Object.entries(productTranslatedCustomFields.value || {})
    .filter(([key]) => key.startsWith('PIM_PRODUCT_ATTACHMENT_CUSTOM_FIELD_sdb'))

  // Group by index (which will be the files)
  const groupedItems = relevantItems.reduce((acc, [key, value]) => {
    const match = key.match(/PIM_PRODUCT_ATTACHMENT_CUSTOM_FIELD_sdbCH_(\d+)/)
    if (!match) return acc
    const index = match[1]

    if (!acc[index]) {
      acc[index] = { id: `sdbCH_${index}` }
    }

    if (key.endsWith('_URL')) {
      acc[index].url = value
    } else if (key.endsWith('_FILENAME')) {
      // Remove the file extension from the value
      const cleanMediaName = value?.replace(/\.[^.]*$/, '')
      acc[index].media_name = cleanMediaName

      // Extract language code from the filename (after the last underscore, before the extension)
      const language = value.split('_').pop()
        .split('.')[0]
      acc[index].languages = [language]
    }

    return acc
  }, {})

  if (!Object.keys(groupedItems).length) {
    return []
  }

  const files: Files = Object.values(groupedItems)

  // set the title removes files[0].languages[0] from files[0].media_name
  if (!files) {
    return
  }
  const title = files[0].media_name.replace(`_${files[0].languages[0]}`, '')

  return [
    {
      id: 'downloads',
      title: { 'de-DE': title },
      files: files,
    },
  ]
})

// ACCESSORIES
const accessoriesData = computed<Accessory[]>(() => {
  const crossSellings = props.product.crossSellings || []

  return crossSellings.map((item) => {
    return {
      id: item.crossSelling.id,
      heading: t('product.accesories'),
      title: item.crossSelling.translated.name,
      products: item.products || [],
    }
  })
})

const hasAccesories = computed(() => {
  return accessoriesData.value.some((accessory: Accessory) => accessory.products.length > 0)
})

const activeStep = ref<number>(0)
const steps = ref< HTMLElement[]>([])
const ctx = useGsap.context(() => {})
function initScrolltrigger() {
  const mm = gsap.matchMedia()

  let sensorOffset = '146px'
  mm.add('(min-width: 1536px)', () => {
    sensorOffset = '350px'
  })

  steps.value.forEach((step, index) => {
    ctx.add(() => {
      ScrollTrigger.create({
        trigger: step,
        start: 'top ' + sensorOffset,
        end: 'bottom ' + sensorOffset,
        markers: false,
        onToggle: (self) => {
          if (self.isActive) {
            activeStep.value = index
          }
        },
      })
    })
  })
}

onMounted(() => {
  initScrolltrigger()
})

onUnmounted(() => {
  ctx?.revert()
})
</script>

<style>
.table-lists {
 li {
    margin-bottom: 0.25rem;
 }
}
</style>
